import React from 'react';

import classNames from 'classnames';

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'violet';

interface BadgeProps {
    children: React.ReactNode;
    tone?: BadgeTone;
    size?: 'sm' | 'md';
    dot?: boolean;
    className?: string;
}

const toneClasses: Record<BadgeTone, string> = {
    neutral: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 ring-slate-200 dark:ring-slate-700',
    accent: 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 ring-teal-200 dark:ring-teal-800',
    success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-800',
    warning: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 ring-amber-200 dark:ring-amber-800',
    danger: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 ring-red-200 dark:ring-red-800',
    info: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 ring-sky-200 dark:ring-sky-800',
    violet: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 ring-cyan-200 dark:ring-cyan-800',
};

const dotClasses: Record<BadgeTone, string> = {
    neutral: 'bg-slate-400 dark:bg-slate-500',
    accent: 'bg-teal-500 dark:bg-teal-400',
    success: 'bg-emerald-500 dark:bg-emerald-400',
    warning: 'bg-amber-500 dark:bg-amber-400',
    danger: 'bg-red-500 dark:bg-red-400',
    info: 'bg-sky-500 dark:bg-sky-400',
    violet: 'bg-cyan-500 dark:bg-cyan-400',
};

const Badge: React.FC<BadgeProps> = ({ children, tone = 'neutral', size = 'sm', dot = false, className }) => {
    return (
        <span
            className={classNames(
                'inline-flex items-center gap-1.5 font-medium rounded-full ring-1 ring-inset whitespace-nowrap',
                toneClasses[tone],
                size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
                className,
            )}
        >
            {dot && <span aria-hidden className={classNames('w-1.5 h-1.5 rounded-full shrink-0', dotClasses[tone])} />}
            {children}
        </span>
    );
};

export default Badge;
